import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SafeIcon from '../common/SafeIcon';

const layers = [
  ['Google Profile', 'The source of truth for your business profile, services, location, reviews, and local proof.'],
  ['Review Signals', 'The words customers already use to describe why they trust you.'],
  ['Visibility Gaps', 'The places where search, maps, AI answers, or competitors show what needs attention.'],
  ['Action Plan', 'The practical next steps that tell you what to improve first.'],
];

const crew = [
  {
    name: 'Mabel Writes',
    description: 'Turns reviews into helpful website articles.',
    path: '/ai-team/mabel',
    icon: 'FileText',
  },
  {
    name: 'Ruby Replies',
    description: 'Helps draft thoughtful review replies.',
    path: '/ai-team/ruby',
    icon: 'MessageSquare',
  },
  {
    name: 'Annie Amplifies',
    description: 'Turns trust signals into social posts for the platforms that matter.',
    path: '/ai-team/annie',
    icon: 'Share2',
  },
];

function Label({ children }) {
  return <p className="mb-4 text-xs font-black uppercase tracking-[0.28em] text-[#409FA1]">{children}</p>;
}

function CTA({ children = 'Get Upgraded', className = '' }) {
  return <Link to="/create-access" className={`inline-flex items-center justify-center rounded-full bg-[#F49558] px-8 py-4 text-base font-black text-white shadow-lg shadow-[#F49558]/20 transition-all hover:-translate-y-1 hover:bg-[#D35E0E] hover:shadow-xl ${className}`}>{children}</Link>;
}

export default function LocalTrustEngine() {
  return (
    <div className="min-h-screen flex flex-col bg-[#F7F8F7] text-[#101F1F] font-sans">
      <Navbar />
      <main className="flex-grow overflow-hidden">
        <section className="relative px-6 pb-20 pt-32 lg:px-8 lg:pb-28 lg:pt-40">
          <div className="absolute left-1/2 top-20 -z-10 h-72 w-72 -translate-x-1/2 rounded-full bg-[#409FA1]/10 blur-3xl" />
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <Label>THE LOCAL TRUST ENGINE</Label>
              <h1 className="max-w-4xl text-4xl font-black tracking-tight text-[#101F1F] sm:text-5xl lg:text-7xl">
                Your Google Profile Becomes the Source.
                <span className="mt-2 block text-[#244A49]">Your Reviews Become the Action Plan.</span>
              </h1>
              <p className="mt-7 max-w-3xl text-lg leading-8 text-[#244A49] lg:text-xl">
                Most local businesses already have proof hiding in their Google profile, reviews, photos, rankings, and customer feedback. RepUpgrade organizes that proof into a Reputation Report & Action Plan so you know what to improve next.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <CTA />
                <a href="#how-it-works" className="inline-flex items-center justify-center rounded-full border border-[#244A49]/15 bg-white px-8 py-4 text-base font-black text-[#244A49] transition-all hover:-translate-y-1 hover:border-[#409FA1] hover:text-[#409FA1]">
                  See What It Pulls Together
                </a>
              </div>
            </div>

            <div className="rounded-[2rem] border border-[#244A49]/10 bg-white p-5 shadow-xl shadow-[#244A49]/5">
              <div className="rounded-[1.5rem] bg-[#101F1F] p-8 text-white">
                <Label>REPORT SNAPSHOT</Label>
                <h2 className="text-3xl font-black tracking-tight">Clear proof. Clear gaps. Clear next steps.</h2>
                <p className="mt-5 text-base leading-7 text-white/75">
                  Google unlocks the core data. Other review platforms add more context. The report turns the signals into a simple plan.
                </p>
                <div className="mt-8 grid gap-3">
                  {layers.map(([title, text]) => (
                    <div key={title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-sm font-black text-[#F6D541]">{title}</p>
                      <p className="mt-2 text-sm leading-6 text-white/75">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="px-6 py-20 lg:px-8 bg-white border-y border-[#244A49]/5">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <Label>WHAT THE REPORT CONNECTS</Label>
              <h2 className="text-3xl font-black tracking-tight sm:text-5xl">The scattered pieces become one clear view.</h2>
              <p className="mt-5 text-lg leading-8 text-[#244A49]">
                RepUpgrade pulls together the signals that shape local trust, then turns them into action steps you can review and use.
              </p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {layers.map(([title, text], index) => (
                <div key={title} className="rounded-[2rem] border border-[#244A49]/10 bg-[#F7F8F7] p-7 shadow-sm">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#409FA1]/10 text-sm font-black text-[#409FA1]">{index + 1}</span>
                  <h3 className="mt-6 text-xl font-black text-[#101F1F]">{title}</h3>
                  <p className="mt-3 text-base leading-7 text-[#244A49]/85">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 py-20 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div className="rounded-[2rem] border border-[#244A49]/10 bg-white p-8 shadow-sm">
              <Label>THE WORKING EXAMPLE</Label>
              <h2 className="text-3xl font-black tracking-tight sm:text-4xl">Matt’s Tree Service</h2>
              <p className="mt-4 text-lg leading-8 text-[#244A49]/85">
                A home service business does not need more noise. It needs the truth of the business organized so customers can trust it faster.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {['Tree Removal', 'Tree Trimming', 'Stump Grinding', 'Storm Cleanup'].map(service => (
                  <div key={service} className="rounded-2xl bg-[#F7F8F7] p-4 font-bold text-[#244A49]">{service}</div>
                ))}
              </div>
            </div>
            <div className="rounded-[2rem] border border-[#244A49]/10 bg-white p-8 shadow-sm">
              <Label>REVIEW PROOF THEMES</Label>
              <div className="grid gap-3">
                {['Fast response', 'Exceptional cleanup', 'Risky removals handled safely', 'Professional communication', 'Fair pricing'].map((theme, index) => (
                  <div key={theme} className="flex items-center gap-4 rounded-2xl bg-[#F7F8F7] p-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#409FA1]/10 text-sm font-black text-[#409FA1]">{index + 1}</span>
                    <p className="font-black text-[#101F1F]">{theme}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 py-20 lg:px-8 bg-[#101F1F] text-white">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-4xl text-center">
              <Label>MEET THE CREW</Label>
              <h2 className="text-3xl font-black tracking-tight sm:text-5xl">The report finds the proof. The crew helps turn it into action.</h2>
              <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-white/75">
                Mabel, Ruby, and Annie help turn customer words, business wins, and trust signals into useful content you can approve and use.
              </p>
            </div>

            <div className="mt-12 grid gap-7 lg:grid-cols-3">
              {crew.map(member => (
                <Link key={member.name} to={member.path} className="rounded-[2rem] border border-white/10 bg-white/5 p-7 text-white shadow-xl shadow-[#101F1F]/10 transition-all hover:-translate-y-1 hover:bg-white/10">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10">
                    <SafeIcon name={member.icon} className="h-7 w-7 text-[#F6D541]" />
                  </div>
                  <h3 className="mt-8 text-2xl font-black tracking-tight">{member.name}</h3>
                  <p className="mt-4 text-base leading-7 text-white/75">{member.description}</p>
                  <span className="mt-6 inline-flex text-sm font-black text-white">Learn more →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 py-20 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-5xl rounded-[2rem] border border-[#244A49]/10 bg-white p-8 text-center shadow-xl shadow-[#244A49]/5 lg:p-12">
            <Label>THE ACTION PLAN</Label>
            <h2 className="text-3xl font-black tracking-tight sm:text-5xl">Every signal becomes a step.</h2>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-[#244A49]">
              RepUpgrade turns your reviews, rankings, visibility, and customer feedback into a practical plan your team can actually use.
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <CTA className="px-10" />
              <Link to="/how-it-works" className="inline-flex items-center justify-center rounded-full border border-[#244A49]/10 bg-white px-8 py-4 text-base font-black text-[#244A49] transition-all hover:-translate-y-1 hover:border-[#409FA1] hover:text-[#409FA1]">
                How It Works
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}